import { noop } from 'lodash';
import { dispatchAction } from '@ardoq/rxbeach';
import { returnNull } from '@ardoq/common-helpers';
import { ViewIds } from '@ardoq/api-types';
import { viewLegendCommands } from '@ardoq/view-legend';
import {
  DisabledZoomControls,
  updateViewContext,
  updateViewProperties,
} from '@ardoq/view-settings';
import { viewHasLegend } from 'views/metaInfoTabs';

type ViewPaneAction = (() => void) | null;

type RegisterViewPaneArgs = {
  viewId: ViewIds;
  viewProperties: Record<string, unknown>;
  isLegendActive: boolean;
  /** Zoom/optimise handlers; null (the default) hides the control. */
  zoomIn?: ViewPaneAction;
  zoomOut?: ViewPaneAction;
  optimizeLayout?: ViewPaneAction;
  /** The view's own DOM container; null (the default) for views that have none. */
  getContainer?: () => HTMLElement | null;
  /**
   * Flattens whatever the view has drawn, for export. Omitted (the default) leaves the context
   * without one, which is what `pngExporter` reads as "nothing to export".
   */
  createExportCanvas?: () => OffscreenCanvas | null;
  /** Identifies the pane instance, as the header's view context carries it. */
  viewInstanceId?: string;
};

/**
 * Registers view properties (for the settings panel) and the view-pane context (the
 * legend toggle, zoom/optimise controls) for views that draw their own canvas — Assistant
 * Blocks, Assistant Diagram, the Flexible Block Diagram and the 3D Surface — keeping the
 * view-pane concern out of the component. `getGraphComponent` is null: these views
 * render their own canvas rather than a yFiles graph component.
 *
 * Publishing properties without a context is the trap this exists to close. `viewModifiers$`
 * holds one context for the whole studio and never clears it on a view change, so a view that
 * skips it inherits the previous one's — and `DEFAULT_VIEW_CONTEXT`'s zoom handlers are `noop`
 * rather than null, which renders the controls instead of hiding them.
 */
export const registerViewPane = ({
  viewId,
  viewProperties,
  isLegendActive,
  zoomIn = null,
  zoomOut = null,
  optimizeLayout = null,
  getContainer = returnNull,
  createExportCanvas,
  viewInstanceId,
}: RegisterViewPaneArgs): void => {
  dispatchAction(updateViewProperties({ viewId, viewProperties }));
  dispatchAction(
    updateViewContext({
      getContainer,
      createExportCanvas,
      getGraphComponent: returnNull,
      /* `rightControls$` builds the header's Toggle legend off the context rather than off
         `metaInfoTabs`, and its only test for "this view has no legend" is
         `toggleLegend === noop` - the `DEFAULT_VIEW_CONTEXT` value. A live closure here is
         therefore an enabled toggle, whatever the view declared. Read off `features.hasLegend`
         so the declaration a view already makes is the one that decides. */
      toggleLegend: viewHasLegend(viewId)
        ? () =>
            viewLegendCommands.updateViewLegendSettings({
              viewId,
              isActive: !isLegendActive,
            })
        : noop,
      zoomIn,
      zoomOut,
      optimizeLayout,
      zoomControls: null,
      zoomControlsDisabledState: DisabledZoomControls.NONE,
      isLegendActive,
      viewInstanceId,
    })
  );
};
